import { useState, useEffect, useCallback, useRef } from 'react';
import {
  VideoEditConfig,
  ClipEditInfo,
  ExportVideoRequest,
  ExportStatus,
  IVideoClip,
  IEpisode,
  ScriptMode
} from '@packages/global';
import { videoExportApi } from '../services/user/videoExportApi';
import { toast } from 'react-hot-toast';
import { getVideoDuration } from '../utils/videoUtils';

// 检查发言者是否为旁白
const isNarrator = (speaker?: string): boolean => {
  if (!speaker) return false;
  const normalizedSpeaker = speaker.trim().toLowerCase();
  // 旁白关键词
  const narratorKeywords = ['narrator', '旁白', '叙述者', 'narrative', 'narration'];
  return narratorKeywords.some(keyword => normalizedSpeaker.includes(keyword));
};

export const useVideoEdit = (episode: IEpisode | null, videoClips: IVideoClip[]) => {
  const [editConfig, setEditConfig] = useState<VideoEditConfig | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportJobId, setExportJobId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // 从视频片段生成默认编辑配置
  const generateDefaultConfig = useCallback(async (): Promise<VideoEditConfig> => {
    let totalDuration = 0;
    const clips: ClipEditInfo[] = [];

    for (const clip of videoClips) {
      // 初始时长 = 脚本时长，默认 5 秒
      // 如果已有音频（此时不会有视频），用音频时长防止截断
      let duration = clip.duration || 5;

      if (clip.scriptAudioUrl) {
        try {
          const audioDuration = await getVideoDuration(clip.scriptAudioUrl);
          if (audioDuration && audioDuration > 0 && audioDuration > duration) {
            duration = audioDuration;
          }
        } catch (error) {
          console.warn(`Failed to get audio duration for ${clip.id}:`, error);
        }
      }

      const clipInfo: ClipEditInfo = {
        clipId: clip.id,
        startTime: totalDuration,
        duration,
        // 根据发言者类型设置音量和对话音量
        // 旁白：视频轨道静音，对话轨道不静音
        // 非旁白：视频轨道不静音，对话轨道静音
        videoVolume: isNarrator(clip.speaker) ? 0 : 1,
        dialogueVolume: isNarrator(clip.speaker) ? 1 : 0,
        showSubtitle: true,
        transition: clips.length > 0 ? { type: 'cut', duration: 0 } : undefined
      };

      clips.push(clipInfo);
      totalDuration += duration;
    }

    return {
      timeline: {
        totalDuration,
        pixelsPerSecond: 20
      },
      tracks: {
        video: { enabled: true, volume: 1 },
        audio: { enabled: true, volume: episode?.scriptMode === ScriptMode.MV ? 0 : 0.8 },
        subtitle: {
          enabled: true,
          style: {
            fontSize: 18,
            color: '#ffffff',
            position: 'bottom',
            background: true,
            appearancePreset: 'white_black',
            fontId: undefined,
            fontName: undefined,
            fontUrl: undefined
          }
        },
        bgm: { enabled: !!episode?.bgmUrl, volume: episode?.scriptMode === ScriptMode.MV ? 0.75 : 0.3 }
      },
      clips,
      export: {
        format: 'mp4',
        quality: 'high',
        resolution: '1920x1080',
        fps: 30
      }
    };
  }, [videoClips, episode]);

  // 初始化 editConfig
  const initializeEditConfig = useCallback(async () => {
    if (!episode?.id || videoClips.length === 0) return;

    setLoading(true);
    try {
      const defaultConfig = await generateDefaultConfig();
      setEditConfig(defaultConfig);
      // 保存到服务器
      await videoExportApi.saveEditConfig(episode.id, defaultConfig);
      console.log('EditConfig 初始化并保存成功');
    } catch (error) {
      console.error('Failed to initialize edit config:', error);
    } finally {
      setLoading(false);
    }
  }, [episode?.id, videoClips.length, generateDefaultConfig]);

  // 加载编辑配置
  const loadEditConfig = useCallback(async () => {
    if (!episode?.id) return;

    setLoading(true);
    try {
      // 优先使用 episode 中的 editConfig
      if (episode.editConfig) {
        setEditConfig(episode.editConfig);
        console.log('使用 episode 中的 editConfig');
        return;
      }

      // 如果 episode 中没有，尝试从 API 加载
      const editConfig = await videoExportApi.getEditConfig(episode.id);
      if (editConfig) {
        setEditConfig(editConfig);
        console.log('从 API 加载 editConfig');
      } else {
        console.log('没有找到 editConfig，等待初始化');
      }
    } catch (error) {
      console.error('Failed to load edit config:', error);
    } finally {
      setLoading(false);
    }
  }, [episode?.id, episode?.editConfig]);

  // 保存编辑配置
  const saveEditConfig = useCallback(async (config: VideoEditConfig) => {
    if (!episode?.id) return;

    setIsSaving(true);
    try {
      await videoExportApi.saveEditConfig(episode.id, config);
      setEditConfig(config);
      toast.success('编辑配置已保存');
    } catch (error) {
      console.error('Failed to save edit config:', error);
      toast.error('保存编辑配置失败');
    } finally {
      setIsSaving(false);
    }
  }, [episode?.id]);

  // 防抖保存的 timer ref
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 保存最新的 editConfig 用于卸载时保存
  const latestEditConfigRef = useRef<VideoEditConfig | null>(null);

  // 更新编辑配置
  const updateEditConfig = useCallback((config: VideoEditConfig) => {
    if (!editConfig) return;

    setEditConfig(config);
    latestEditConfigRef.current = config;

    // 清除之前的定时器
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    // 自动保存（防抖）- 减少到500ms以提高响应性
    saveTimerRef.current = setTimeout(() => {
      saveEditConfig(config);
      saveTimerRef.current = null;
    }, 500);
  }, [editConfig, saveEditConfig]);

  // 导出视频
  const exportVideo = useCallback(async (exportRequest: ExportVideoRequest = {}) => {
    if (!episode?.id || !editConfig) {
      toast.error('缺少必要的配置信息');
      return;
    }

    setIsExporting(true);
    try {
      // 先保存当前配置
      await saveEditConfig(editConfig);

      // 开始导出
      const response = await videoExportApi.exportVideo(episode.id, exportRequest);
      setExportJobId(response.jobId);
      toast.success('视频导出已开始');
    } catch (error) {
      console.error('Failed to export video:', error);
      toast.error('导出视频失败');
      setIsExporting(false);
    }
  }, [episode?.id, editConfig, saveEditConfig]);

  // 重置导出状态
  const resetExport = useCallback(() => {
    setIsExporting(false);
    setExportJobId(null);
  }, []);

  // 刷新单个 clip 的时长信息
  // 规则：只在「有音频 + 没视频 + 音频时长 > 当前时长」时才扩展 duration，其他情况不覆盖
  const refreshClipDuration = useCallback(async (clipId: string) => {
    if (!editConfig) return;

    const clip = videoClips.find(c => c.id === clipId);
    if (!clip) return;

    const clipIndex = editConfig.clips.findIndex(c => c.clipId === clipId);
    if (clipIndex === -1) return;

    const currentClip = editConfig.clips[clipIndex];
    const oldDuration = currentClip.duration;
    let newDuration = oldDuration;

    // 只在没有视频的情况下，音频时长超过当前时长时才扩展，防止音频被截断
    if (clip.scriptAudioUrl && !clip.videoUrl) {
      try {
        const audioDuration = await getVideoDuration(clip.scriptAudioUrl);
        if (audioDuration && audioDuration > 0 && audioDuration > oldDuration) {
          newDuration = audioDuration;
        }
      } catch (error) {
        console.warn(`Failed to get audio duration for ${clipId}:`, error);
      }
    }

    if (oldDuration === newDuration) return;

    const updatedClips = [...editConfig.clips];
    const durationDiff = newDuration - oldDuration;

    updatedClips[clipIndex] = {
      ...currentClip,
      duration: newDuration
    };

    // 更新后续 clips 的 startTime
    for (let i = clipIndex + 1; i < updatedClips.length; i++) {
      updatedClips[i] = {
        ...updatedClips[i],
        startTime: updatedClips[i].startTime + durationDiff
      };
    }

    const newConfig: VideoEditConfig = {
      ...editConfig,
      timeline: {
        ...editConfig.timeline,
        totalDuration: editConfig.timeline.totalDuration + durationDiff
      },
      clips: updatedClips
    };

    setEditConfig(newConfig);

    if (episode?.id) {
      try {
        await videoExportApi.saveEditConfig(episode.id, newConfig);
      } catch (error) {
        console.error('Failed to save edit config after refresh:', error);
      }
    }
  }, [editConfig, videoClips, episode?.id]);

  // 上次刷新的时间戳，避免频繁刷新
  const lastRefreshTimeRef = useRef<number>(0);

  // 刷新所有 clips 的时长信息
  // 规则：只在「有音频 + 没视频 + 音频时长 > 当前时长」时才扩展 duration，其他情况不覆盖
  const refreshAllClipsDuration = useCallback(async () => {
    if (!editConfig || videoClips.length === 0) return;

    // 防止频繁刷新：至少间隔 2 秒
    const now = Date.now();
    if (now - lastRefreshTimeRef.current < 2000) {
      console.log('跳过频繁刷新，距离上次刷新不足2秒');
      return;
    }
    lastRefreshTimeRef.current = now;

    let totalDuration = 0;
    const updatedClips: ClipEditInfo[] = [];
    let hasChanges = false;

    for (const existingClipInfo of editConfig.clips) {
      const clip = videoClips.find(c => c.id === existingClipInfo.clipId);
      if (!clip) continue;

      const oldDuration = existingClipInfo.duration;
      let newDuration = oldDuration;

      // 只在没有视频的情况下，音频时长超过当前时长时才扩展，防止音频被截断
      if (clip.scriptAudioUrl && !clip.videoUrl) {
        try {
          const audioDuration = await getVideoDuration(clip.scriptAudioUrl);
          if (audioDuration && audioDuration > 0 && audioDuration > oldDuration) {
            newDuration = audioDuration;
          }
        } catch (error) {
          console.warn(`Failed to get audio duration for ${clip.id}:`, error);
        }
      }

      if (oldDuration !== newDuration || existingClipInfo.startTime !== totalDuration) {
        hasChanges = true;
      }

      const updatedClipInfo: ClipEditInfo = {
        ...existingClipInfo,
        startTime: totalDuration,
        duration: newDuration
      };

      updatedClips.push(updatedClipInfo);
      totalDuration += newDuration;
    }

    if (!hasChanges) return;

    const newConfig: VideoEditConfig = {
      ...editConfig,
      timeline: {
        ...editConfig.timeline,
        totalDuration
      },
      clips: updatedClips
    };

    setEditConfig(newConfig);

    if (episode?.id) {
      try {
        await videoExportApi.saveEditConfig(episode.id, newConfig);
      } catch (error) {
        console.error('Failed to save edit config after refresh:', error);
      }
    }
  }, [editConfig, videoClips, episode?.id]);

  // 初始化加载配置
  useEffect(() => {
    if (episode) {
      loadEditConfig();
    }
  }, [episode?.id, loadEditConfig]);

  // 组件卸载时保存未保存的更改
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      // 使用 ref 中的最新配置进行保存
      const configToSave = latestEditConfigRef.current;
      if (configToSave && episode?.id) {
        videoExportApi.saveEditConfig(episode.id, configToSave).catch(err => {
          console.error('Failed to save on unmount:', err);
        });
      }
    };
  }, [episode?.id]); // 只依赖 episode?.id，避免频繁重建清理函数

  return {
    editConfig,
    isExporting,
    exportJobId,
    isSaving,
    loading,
    updateEditConfig,
    saveEditConfig,
    exportVideo,
    resetExport,
    loadEditConfig,
    initializeEditConfig,
    refreshClipDuration,
    refreshAllClipsDuration
  };
};
